// src/services/attendanceServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/attendance`;

// Toujours récupérer le token au moment de l'appel
const authHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Content-Type": "application/json",
  };
};

// Utilitaire : force 'YYYY-MM-DD'
const toYMD = (date) => (date ? String(date).slice(0, 10) : "");

// Sécurise les ids
const toId = (value) => Number(value);

/* -------------------------------------------------------
 * POST bulk upsert
 * Enregistre l'appel d'un groupe
 *
 * attendances: [
 *   {
 *     adherent_id,
 *     group_id,
 *     presence,
 *     date_presence
 *   }
 * ]
 * ----------------------------------------------------- */
export const saveAttendance = async (attendances = []) => {
  try {
    const payload = {
      attendances: attendances
        .filter((attendance) => {
          return (
            attendance &&
            attendance.adherent_id &&
            attendance.group_id &&
            attendance.date_presence
          );
        })
        .map((attendance) => ({
          adherent_id: toId(attendance.adherent_id),
          group_id: toId(attendance.group_id),
          presence: Boolean(attendance.presence),
          date_presence: toYMD(attendance.date_presence),
        })),
    };

    const { data } = await axios.post(`${apiUrl}/submitAttendance`, payload, {
      headers: authHeaders(),
    });

    return data;
  } catch (error) {
    console.error(
      "Erreur lors de l'enregistrement de l'appel bulk :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * PUT unitaire
 * Met à jour une seule case :
 * adherent_id + group_id + date_presence
 * ----------------------------------------------------- */
export const setAttendance = async (
  adherentId,
  groupId,
  date,
  presence
) => {
  try {
    const d = toYMD(date);

    const { data } = await axios.put(
      `${apiUrl}/${toId(adherentId)}/${toId(groupId)}/${d}`,
      {
        presence: Boolean(presence),
      },
      {
        headers: authHeaders(),
      }
    );

    return data;
  } catch (error) {
    console.error(
      "Erreur setAttendance unitaire :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * DELETE unitaire
 * Supprime une saisie :
 * adherent_id + group_id + date_presence
 * ----------------------------------------------------- */
export const clearAttendance = async (adherentId, groupId, date) => {
  try {
    const d = toYMD(date);

    const { data } = await axios.delete(
      `${apiUrl}/${toId(adherentId)}/${toId(groupId)}/${d}`,
      {
        headers: authHeaders(),
      }
    );

    return data;
  } catch (error) {
    console.error(
      "Erreur clearAttendance unitaire :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * GET toutes les présences
 * ----------------------------------------------------- */
export const getAllAttendances = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/getAllAttendances`, {
      headers: authHeaders(),
    });

    return data;
  } catch (error) {
    console.error(
      "Erreur getAllAttendances :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * GET présences d'un adhérent
 * ----------------------------------------------------- */
export const getAttendanceList = async (adherentId) => {
  try {
    const { data } = await axios.get(
      `${apiUrl}/getAttendancesByAdherent/${toId(adherentId)}`,
      {
        headers: authHeaders(),
      }
    );

    return data;
  } catch (error) {
    console.error(
      "Erreur getAttendanceList :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * GET présences d'un groupe
 * ----------------------------------------------------- */
export const getAttendancesByGroup = async (groupId) => {
  try {
    const { data } = await axios.get(
      `${apiUrl}/getAttendancesByGroup/${toId(groupId)}`,
      {
        headers: authHeaders(),
      }
    );

    return data;
  } catch (error) {
    console.error(
      "Erreur getAttendancesByGroup :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * GET présences d'un groupe à une date précise
 * Très utile pour précharger l'appel du jour
 * ----------------------------------------------------- */
export const getAttendancesByGroupAndDate = async (groupId, date) => {
  try {
    const d = toYMD(date);

    const { data } = await axios.get(
      `${apiUrl}/getAttendancesByGroupAndDate/${toId(groupId)}/${d}`,
      {
        headers: authHeaders(),
      }
    );

    return data;
  } catch (error) {
    console.error(
      "Erreur getAttendancesByGroupAndDate :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * GET assiduité d'un adhérent
 * Optionnel : groupId pour calculer l'assiduité sur un groupe
 * ----------------------------------------------------- */
export const countAttendance = async (adherentId, groupId = null) => {
  try {
    const query = groupId ? `?group_id=${toId(groupId)}` : "";

    const { data } = await axios.get(
      `${apiUrl}/calculateAssiduity/${toId(adherentId)}${query}`,
      {
        headers: authHeaders(),
      }
    );

    return data;
  } catch (error) {
    console.error(
      "Erreur countAttendance :",
      error?.response?.data || error
    );
    throw error;
  }
};

/* -------------------------------------------------------
 * DELETE mass
 * Attention : supprime toutes les présences
 * ----------------------------------------------------- */
export const deleteAllAttendances = async () => {
  try {
    const { data } = await axios.delete(`${apiUrl}/deleteAll`, {
      headers: authHeaders(),
    });

    return data;
  } catch (error) {
    console.error(
      "Erreur deleteAllAttendances :",
      error?.response?.data || error
    );
    throw error;
  }
};

// DELETE période
// Supprime les appels d'un groupe entre deux dates
export const deleteAttendancesByPeriod = async ({
  groupId,
  startDate,
  endDate,
}) => {
  try {
    const { data } = await axios.delete(`${apiUrl}/deleteByPeriod`, {
      headers: authHeaders(),
      params: {
        group_id: Number(groupId),
        start_date: toYMD(startDate),
        end_date: toYMD(endDate),
      },
    });

    return data;
  } catch (error) {
    console.error(
      "Erreur deleteAttendancesByPeriod :",
      error?.response?.data || error
    );
    throw error;
  }
};